import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { PlusSquare, Share, Smartphone, X } from "lucide-react";
import { isIos, isStandalone, isWebView } from "@/lib/device";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { Logger } from "@/lib/logger";

const log = new Logger("A2HS");

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const ENGAGEMENT_DELAY_MS = 15_000; // 15 secondes
const LIVE_CLASS_RETRY_MS = 30_000;

/**
 * Une séance en visio occupe tout l'écran et n'attend pas : y superposer un panneau plein écran
 * couperait le cours. Le cas n'est pas théorique — on arrive directement sur `/…/live-class/:id`
 * depuis une notification ou un lien, donc le délai d'engagement s'écoule pendant la séance.
 */
function isLiveClassRoute(): boolean {
  if (typeof window === "undefined") return false;
  return /\/live-class\//.test(window.location.pathname);
}

/**
 * Bottom sheet incitant les utilisateurs iOS Safari à ajouter l'application à l'écran d'accueil.
 *
 * Déclenché uniquement sur iOS mobile / tablette, hors mode standalone (PWA déjà installée),
 * hors WebView, après 15s d'engagement si l'utilisateur ne l'a pas masqué récemment.
 */
export function AddToHomeScreenPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    // 1. Vérification de l'environnement iOS
    if (!isIos()) {
      log.debug("A2HS ignoré : l'appareil n'est pas sous iOS.");
      return;
    }

    // 2. Vérification du mode standalone (déjà installé)
    if (isStandalone()) {
      log.debug("A2HS ignoré : l'application est déjà en mode standalone (PWA).");
      return;
    }

    // 3. Vérification des WebView
    if (isWebView()) {
      log.debug("A2HS ignoré : exécution dans une WebView.");
      return;
    }

    // 4. Vérification du stockage local
    try {
      const dismissed = localStorage.getItem(STORAGE_KEYS.A2HS_POPUP_DISMISSED_AT);
      if (dismissed === "never") {
        log.debug("A2HS ignoré : masqué définitivement par l'utilisateur.");
        return;
      }

      if (dismissed) {
        const dismissedAt = Number(dismissed);
        if (!Number.isNaN(dismissedAt) && Date.now() - dismissedAt < SEVEN_DAYS_MS) {
          log.debug("A2HS ignoré : fermé il y a moins de 7 jours.");
          return;
        }
      }
    } catch {
      // Ignorer les erreurs d'accès à localStorage (ex: navigation privée stricte)
    }

    // 5. Délai d'engagement de 15 secondes
    let timer = 0;
    const scheduleCheck = (delay: number) => {
      timer = window.setTimeout(() => {
        // On repousse au lieu d'abandonner : l'utilisateur reste éligible une fois le cours quitté.
        if (isLiveClassRoute()) {
          log.debug("A2HS repoussé : une séance en visio est ouverte.");
          scheduleCheck(LIVE_CLASS_RETRY_MS);
          return;
        }
        setIsVisible(true);
        log.debug("A2HS Bottom Sheet affiché après le délai d'engagement.");
      }, delay);
    };
    scheduleCheck(ENGAGEMENT_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const handleDismissTemporary = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.A2HS_POPUP_DISMISSED_AT, String(Date.now()));
    } catch {
      // ignore
    }
    setIsVisible(false);
    log.debug("A2HS fermé temporairement (7 jours).");
  };

  const handleDismissPermanent = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.A2HS_POPUP_DISMISSED_AT, "never");
    } catch {
      // ignore
    }
    setIsVisible(false);
    log.debug("A2HS masqué définitivement.");
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end pointer-events-none">
          {/* Overlay semi-transparent */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleDismissTemporary}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm pointer-events-auto"
            aria-hidden="true"
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="relative w-full max-w-lg mx-auto bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-t-[2rem] border-t border-slate-200/80 dark:border-slate-800 shadow-2xl px-5 py-6 sm:px-7 sm:py-8 pb-[max(2rem,calc(env(safe-area-inset-bottom)+1rem))] pointer-events-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="a2hs-title"
          >
            {/* Barre de poignée (drag handle indicator) */}
            <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mb-5 opacity-75" />

            {/* Bouton de fermeture (croix) */}
            <button
              type="button"
              onClick={handleDismissTemporary}
              aria-label="Fermer"
              className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {step === 1 ? (
              /* ── Étape 1 : Teaser ──────────────────────────────── */
              <div className="space-y-5 pt-1">
                <div className="flex items-start gap-3.5 sm:gap-4">
                  <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-2xl bg-fuchsia-50 dark:bg-fuchsia-950/50 border border-fuchsia-100 dark:border-fuchsia-900/40 text-fuchsia-600 dark:text-fuchsia-400 flex items-center justify-center shrink-0 shadow-xs">
                    <Smartphone className="w-6 h-6 sm:w-6.5 sm:h-6.5" />
                  </div>
                  <div className="space-y-1 flex-1 min-w-0 pr-6">
                    <h3
                      id="a2hs-title"
                      className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight leading-snug"
                    >
                      Installez Succeschool sur votre écran d'accueil
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                      Accédez à Succeschool en un clic, comme une vraie application&nbsp;!
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full py-3.5 px-5 bg-[#c026d3] hover:bg-[#a21caf] active:scale-[0.98] text-white font-semibold rounded-xl shadow-lg shadow-fuchsia-600/25 transition-all text-center text-sm sm:text-base cursor-pointer"
                  >
                    Oui, installer
                  </button>
                  <button
                    type="button"
                    onClick={handleDismissTemporary}
                    className="w-full py-3.5 px-5 bg-slate-100 hover:bg-slate-200/90 dark:bg-slate-800 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-300 font-semibold rounded-xl border border-slate-200/70 dark:border-slate-700 active:scale-[0.98] transition-all text-center text-sm sm:text-base cursor-pointer"
                  >
                    Non merci
                  </button>
                </div>
              </div>
            ) : (
              /* ── Étape 2 : Instructions ────────────────────────── */
              <div className="space-y-5 pt-1">
                <div className="space-y-1 pr-6">
                  <h3
                    id="a2hs-title"
                    className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight"
                  >
                    Encore une étape
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Suivez ces 3 étapes simples dans Safari :
                  </p>
                </div>

                <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 sm:p-5 border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3.5">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#003366]/10 dark:bg-fuchsia-500/20 text-[#003366] dark:text-fuchsia-300 font-bold text-xs sm:text-sm flex items-center justify-center shrink-0">
                      1
                    </div>
                    <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-normal">
                      Appuyez sur l'icône de partage{" "}
                      <span className="inline-flex items-center justify-center p-1 bg-white dark:bg-slate-700 rounded-lg text-fuchsia-600 dark:text-fuchsia-400 border border-slate-200/80 dark:border-slate-600 shadow-2xs align-middle mx-1">
                        <Share className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </span>{" "}
                      en bas de votre écran
                    </p>
                  </div>

                  <div className="flex items-center gap-3.5">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#003366]/10 dark:bg-fuchsia-500/20 text-[#003366] dark:text-fuchsia-300 font-bold text-xs sm:text-sm flex items-center justify-center shrink-0">
                      2
                    </div>
                    <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-normal">
                      Faites défiler et sélectionnez{" "}
                      <span className="inline-flex items-center gap-1 font-semibold text-slate-900 dark:text-white bg-white dark:bg-slate-700 px-1.5 py-0.5 rounded-lg border border-slate-200/80 dark:border-slate-600 shadow-2xs">
                        <PlusSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-fuchsia-600 dark:text-fuchsia-400 inline" />
                        « Sur l'écran d'accueil »
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-3.5">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#003366]/10 dark:bg-fuchsia-500/20 text-[#003366] dark:text-fuchsia-300 font-bold text-xs sm:text-sm flex items-center justify-center shrink-0">
                      3
                    </div>
                    <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-normal">
                      Appuyez sur{" "}
                      <span className="font-bold text-slate-900 dark:text-white">« Ajouter »</span>{" "}
                      en haut à droite
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <button
                    type="button"
                    onClick={handleDismissTemporary}
                    className="w-full py-3.5 px-5 bg-[#c026d3] hover:bg-[#a21caf] active:scale-[0.98] text-white font-semibold rounded-xl shadow-lg shadow-fuchsia-600/25 transition-all text-center text-sm sm:text-base cursor-pointer"
                  >
                    J'ai compris
                  </button>
                  <button
                    type="button"
                    onClick={handleDismissPermanent}
                    className="w-full py-3.5 px-5 bg-slate-100 hover:bg-slate-200/90 dark:bg-slate-800 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-300 font-semibold rounded-xl border border-slate-200/70 dark:border-slate-700 active:scale-[0.98] transition-all text-center text-sm sm:text-base cursor-pointer"
                  >
                    Ne plus afficher
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
